// src/admin/FormEditor/store/formEditorSlice.js
// Redux slice for form editor state management
// Handles all form-related operations:
// - Form metadata updates
// - Question CRUD operations
// - Report metadata
// - Question reordering

import { createSlice } from "@reduxjs/toolkit";
import {
  createDefaultOption,
  createDefaultQuestion,
  prepareNewFormMetadata,
} from "../utils/formTemplates";

export const getInitialState = (purpose = "Event") => ({
  formMetadata: prepareNewFormMetadata(purpose),
  formReportMetadata: {
    to: "",
    cc: "",
    bcc: "",
  },
});

const initialState = getInitialState();

export const selectByPath = (state, objectHierarchyPath) => {
  const keys = objectHierarchyPath.split("/");
  let nestedItem = state;

  for (let i = 0; i < keys.length; i++) {
    nestedItem = nestedItem[keys[i]];
  }

  return nestedItem;
};

const formEditorSlice = createSlice({
  name: "formEditor",
  initialState,
  reducers: {
    resetFormEditor: (state, action) => {
      const { purpose } = action.payload;
      return getInitialState(purpose);
    },

    genericUpdate(state, action) {
      const { path, value } = action.payload;
      // Split the path into individual keys
      const keys = path.split("/");
      let currentState = state;

      // Traverse the state object according to the path
      for (let i = 0; i < keys.length - 1; i++) {
        // Move currentState to the next level in the state object
        currentState = currentState[keys[i]];
      }
      const update = {};
      update[keys[keys.length - 1]] = value;
      // currentState = { ...currentState, ...update };
      currentState[keys[keys.length - 1]] = value;
    },

    removeOption: (state, action) => {
      const { questionIndex, optionIndex } = action.payload;
      state.formMetadata.questions[questionIndex].options.splice(
        optionIndex,
        1
      );
    },

    updateOption: (state, action) => {
      const { questionIndex, optionIndex, updates } = action.payload;
      const options = state.formMetadata.questions[questionIndex].options;
      options[optionIndex] = { ...options[optionIndex], ...updates };
    },

    addOption: (state, action) => {
      const { questionIndex } = action.payload;
      const option = createDefaultOption(
        state.formMetadata.supported_languages
      );
      state.formMetadata.questions[questionIndex].options.push(option);
    },

    reorderOptions: (state, action) => {
      const { questionIndex, newOrder } = action.payload;
      state.formMetadata.questions[questionIndex].options = newOrder;
    },

    updateFormMetadata: (state, action) => {
      const { updates } = action.payload;
      state.formMetadata = { ...state.formMetadata, ...updates };
    },

    addQuestion: (state, action) => {
      // Create a new question with default type 'text'
      const { question } = action.payload;
      const newQuestion =
        question ||
        createDefaultQuestion(
          state.formMetadata.supported_languages,
          state.formMetadata.purpose,
          "text",
          {}
        );
      state.formMetadata.questions.push(newQuestion);
    },
    updateQuestion: (state, action) => {
      const { questionIndex, updates } = action.payload;
      const questions = state.formMetadata.questions;
      questions[questionIndex] = { ...questions[questionIndex], ...updates };
    },
    removeQuestion: (state, action) => {
      const { questionIndex } = action.payload;
      state.formMetadata.questions.splice(questionIndex, 1);
    },
    reorderQuestions: (state, action) => {
      state.formMetadata.questions = action.payload;
    },
    updateFormReportMetadata: (state, action) => {
      const { updates } = action.payload;
      state.formReportMetadata = { ...state.formReportMetadata, ...updates };
    },
  },
});

export const {
  resetFormEditor,
  genericUpdate,
  removeOption,
  updateOption,
  addOption,
  reorderOptions,

  updateFormMetadata,
  addQuestion,
  updateQuestion,
  removeQuestion,
  reorderQuestions,
  updateFormReportMetadata,
} = formEditorSlice.actions;

export default formEditorSlice.reducer;
